import * as XLSX from 'xlsx'

/**
 * Export menu data to Excel file
 */
export const exportMenuToExcel = (items, groups, fileName = 'menu.xlsx') => {
  // Prepare items data
  const itemsData = items.map((item) => ({
    'ID': item.id || '',
    'Title': item.title || '',
    'Description': item.description || '',
    'Price': item.price || '',
    'Group': item.group_obj?.name || '',
    'Image URL': item.image || '',
  }))

  // Prepare groups data
  const groupsData = groups.map((group) => ({
    'ID': group.id || '',
    'Name': group.name || '',
    'Order': group.order || '',
  }))

  // Create workbook
  const wb = XLSX.utils.book_new()

  // Add sheets
  const itemsSheet = XLSX.utils.json_to_sheet(itemsData)
  const groupsSheet = XLSX.utils.json_to_sheet(groupsData)

  XLSX.utils.book_append_sheet(wb, itemsSheet, 'Items')
  XLSX.utils.book_append_sheet(wb, groupsSheet, 'Groups')

  // Generate and download
  XLSX.writeFile(wb, fileName)
}

/**
 * Import menu data from Excel file
 * Returns data from Excel including groups and items
 */
export const importMenuFromExcel = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result)
        const workbook = XLSX.read(data, { type: 'array' })

        // Read sheets - try both 'Items' and 'Dishes' for backward compatibility
        const itemsSheet = workbook.Sheets['Items'] || workbook.Sheets['Dishes']
        const groupsSheet = workbook.Sheets['Groups']

        if (!itemsSheet) {
          reject(new Error('Items sheet not found in Excel file'))
          return
        }

        // Parse sheets
        const itemsJson = XLSX.utils.sheet_to_json(itemsSheet)
        const groupsJson = groupsSheet ? XLSX.utils.sheet_to_json(groupsSheet) : []

        // Convert to app format
        const groups = groupsJson.map((row) => ({
          name: row['Name'] || '',
          order: row['Order'] || null,
        }))

        // Convert items with references to groups by name
        const items = itemsJson.map((row) => {
          return {
            title: row['Title'] || '',
            description: row['Description'] || '',
            price: parseFloat(row['Price']) || 0,
            group_name: row['Group'] || '',
            image: row['Image URL'] || '',
          }
        })

        resolve({
          groups,
          items,
        })
      } catch (error) {
        reject(error)
      }
    }

    reader.onerror = (error) => {
      reject(error)
    }

    reader.readAsArrayBuffer(file)
  })
}

/**
 * Generate a template Excel file with example data (Fruits & Vegetables Shop)
 */
export const generateFruitsVegetablesTemplate = () => {
  const items = [
    {
      'Title': 'Fresh Apples',
      'Description': 'Crisp and sweet red apples from local farms',
      'Price': 3.99,
      'Group': 'Fruits',
      'Image URL': '',
    },
    {
      'Title': 'Bananas',
      'Description': 'Ripe yellow bananas, perfect for smoothies',
      'Price': 2.49,
      'Group': 'Fruits',
      'Image URL': '',
    },
    {
      'Title': 'Oranges',
      'Description': 'Juicy Valencia oranges, full of vitamin C',
      'Price': 4.99,
      'Group': 'Fruits',
      'Image URL': '',
    },
    {
      'Title': 'Strawberries',
      'Description': 'Fresh sweet strawberries, locally grown',
      'Price': 5.99,
      'Group': 'Berries',
      'Image URL': '',
    },
    {
      'Title': 'Blueberries',
      'Description': 'Antioxidant-rich blueberries',
      'Price': 6.99,
      'Group': 'Berries',
      'Image URL': '',
    },
    {
      'Title': 'Tomatoes',
      'Description': 'Fresh vine-ripened tomatoes',
      'Price': 3.49,
      'Group': 'Vegetables',
      'Image URL': '',
    },
    {
      'Title': 'Carrots',
      'Description': 'Crunchy organic carrots',
      'Price': 2.99,
      'Group': 'Vegetables',
      'Image URL': '',
    },
    {
      'Title': 'Broccoli',
      'Description': 'Fresh green broccoli crowns',
      'Price': 3.99,
      'Group': 'Vegetables',
      'Image URL': '',
    },
    {
      'Title': 'Lettuce',
      'Description': 'Crisp romaine lettuce',
      'Price': 2.49,
      'Group': 'Leafy Greens',
      'Image URL': '',
    },
    {
      'Title': 'Spinach',
      'Description': 'Fresh baby spinach leaves',
      'Price': 3.99,
      'Group': 'Leafy Greens',
      'Image URL': '',
    },
  ]

  const groups = [
    { 'Name': 'Fruits', 'Order': 1 },
    { 'Name': 'Berries', 'Order': 2 },
    { 'Name': 'Vegetables', 'Order': 3 },
    { 'Name': 'Leafy Greens', 'Order': 4 },
  ]

  // Create workbook
  const wb = XLSX.utils.book_new()

  // Add instruction sheet
  const instructions = [
    ['MENU IMPORT TEMPLATE - Fruits & Vegetables Shop'],
    [''],
    ['Instructions:'],
    ['1. Fill in the data in the sheets below (Items, Groups)'],
    ['2. For Items: Make sure Group names match exactly with Groups sheet'],
    ['3. Price should be a number (e.g., 3.99)'],
    ['4. Save the file and import it using the Import button in the application'],
    [''],
    ['Notes:'],
    ['- ID column will be ignored during import (new IDs will be generated)'],
    ['- Image URL can be left empty or use a valid image URL'],
    ['- You can delete example data and add your own'],
  ]

  const instructionsSheet = XLSX.utils.aoa_to_sheet(instructions)
  XLSX.utils.book_append_sheet(wb, instructionsSheet, 'Instructions')

  // Add sheets
  const itemsSheet = XLSX.utils.json_to_sheet(items)
  const groupsSheet = XLSX.utils.json_to_sheet(groups)

  XLSX.utils.book_append_sheet(wb, itemsSheet, 'Items')
  XLSX.utils.book_append_sheet(wb, groupsSheet, 'Groups')

  // Generate and download
  XLSX.writeFile(wb, 'menu-template-fruits-vegetables.xlsx')
}
